/**
 * Protected Route Component
 *
 * A route wrapper that requires authentication.
 * Redirects to login page if user is not authenticated.
 * Shows loading state while auth state is being determined.
 */

import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { PageLoader } from '@/components/ui';

/**
 * Props for ProtectedRoute component
 */
export interface ProtectedRouteProps {
  /** Child components to render when authenticated */
  children: ReactNode;
  /** Custom redirect path (defaults to /login) */
  redirectTo?: string;
}

/**
 * Protected route component that requires authentication
 *
 * @example
 * ```tsx
 * <Routes>
 *   <Route path="/login" element={<LoginPage />} />
 *   <Route
 *     path="/profile"
 *     element={
 *       <ProtectedRoute>
 *         <ProfilePage />
 *       </ProtectedRoute>
 *     }
 *   />
 * </Routes>
 * ```
 */
export const ProtectedRoute = ({ children, redirectTo = '/login' }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();

  // Show loading state while auth state is being determined
  if (loading) {
    return <PageLoader message="Authenticating..." />;
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to={redirectTo} replace />;
  }

  // Render protected content if authenticated
  return <>{children}</>;
};
